import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * My Memos Dashboard
 *
 * Client-facing page to view and track personal memo requests.
 * Uses table layout consistent with Financial Hub.
 */
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { apiClient } from '../../utils/apiClient';
import { MemoHelpers } from '../../utils/memoHelpers';
import { MemoStatus, MEMO_STATUS_LABELS } from '../../types/memo';
import { notify } from '../../utils/notifications';
import { getCertificateUrl } from '../../utils/certificateUrls';
import { BaseModal } from '../../components/shared/BaseModal';
import { FaExclamationTriangle, FaTimes, FaEye, FaExternalLinkAlt, FaSync } from 'react-icons/fa';
import { Button, TableContainer, Table, Th, Td, Tr, SortableTh, FiltersContainer, FilterGroup, Label, Input, Select, EmptyState, EmptyStateIcon, EmptyStateText } from '../Admin/FinancialHub/SharedComponents';
import styled from 'styled-components';
import { FaGem } from 'react-icons/fa';
// Page-specific styled components
const HeroSection = styled.section `
  padding: 48px 24px 32px;
  text-align: center;
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
  border-bottom: 1px solid var(--border-color);
`;
const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 38px;
  font-weight: 600;
  letter-spacing: 0.03em;
  margin-bottom: 10px;
  color: var(--text-primary);

  span {
    color: var(--accent-gold, #996515);
  }

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;
const HeroSubtitle = styled.p `
  color: var(--text-secondary);
  font-size: 15px;
  max-width: 480px;
  margin: 0 auto;
`;
const MainContent = styled.div `
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px 24px;

  @media (max-width: 768px) {
    padding: 0 16px 16px;
  }
`;
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  max-width: 1200px;
  margin: -24px auto 32px;
  padding: 0 24px;
  position: relative;
  z-index: 10;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;
const StatCard = styled.div `
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--accent-gold, #B8860B), var(--accent-gold-light, #D4A634));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover {
    border-color: var(--accent-gold-border, rgba(184, 134, 11, 0.25));
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .stat-value {
    font-family: 'Playfair Display', 'Adamina', serif;
    font-size: 28px;
    font-weight: 600;
    color: var(--accent-gold, #996515);
    margin-bottom: 4px;
  }

  .stat-label {
    color: var(--text-secondary);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;
const Section = styled.div `
  margin-bottom: 1.5rem;
`;
const SectionHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;
const SectionTitle = styled.h2 `
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;
const ResultsCount = styled.span `
  font-size: 0.875rem;
  color: var(--text-tertiary);
  font-weight: normal;
  margin-left: 0.5rem;
`;
const MemoStatusBadge = styled.span `
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${props => {
    switch (props.$status) {
        case MemoStatus.PENDING: return '#fef3c7';
        case MemoStatus.APPROVED: return '#d1fae5';
        case MemoStatus.SHIPPED: return '#dbeafe';
        case MemoStatus.WITH_CLIENT: return '#e0e7ff';
        case MemoStatus.RETURNED: return '#f3f4f6';
        case MemoStatus.SOLD: return '#d1fae5';
        case MemoStatus.REJECTED: return '#fee2e2';
        case MemoStatus.CANCELLED: return '#f3f4f6';
        default: return '#f3f4f6';
    }
}};
  color: ${props => {
    switch (props.$status) {
        case MemoStatus.PENDING: return '#92400e';
        case MemoStatus.APPROVED: return '#065f46';
        case MemoStatus.SHIPPED: return '#1e40af';
        case MemoStatus.WITH_CLIENT: return '#3730a3';
        case MemoStatus.RETURNED: return '#374151';
        case MemoStatus.SOLD: return '#065f46';
        case MemoStatus.REJECTED: return '#991b1b';
        case MemoStatus.CANCELLED: return '#6b7280';
        default: return '#374151';
    }
}};
`;
const OverdueBadge = styled.span `
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  background: #fee2e2;
  color: #991b1b;
  margin-left: 0.5rem;
`;
const ActionButtonGroup = styled.div `
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;
const ActionButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
  background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(184, 134, 11, 0.1)'};
  border-color: ${props => props.$danger ? '#ef4444' : '#B8860B'};
  color: ${props => props.$danger ? '#ef4444' : '#B8860B'};

  &:hover {
    background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(184, 134, 11, 0.2)'};
  }
`;
const CertLink = styled.a `
  color: var(--accent-gold);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;

  &:hover {
    text-decoration: underline;
  }
`;
const DiamondCell = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;
const DiamondTitle = styled.span `
  font-weight: 600;
  color: var(--text-primary);
`;
const DiamondSpecs = styled.span `
  font-size: 0.75rem;
  color: var(--text-secondary);
`;
const PriceCell = styled.span `
  font-weight: 600;
  color: var(--accent-gold);
  font-family: 'JetBrains Mono', monospace;
`;
const DateCell = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;
const DaysIndicator = styled.span `
  font-size: 0.7rem;
  font-weight: 600;
  color: ${props => props.$overdue ? '#ef4444' : props.$urgent ? '#f59e0b' : '#10b981'};
`;
const LoadingContainer = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
`;
const ErrorMessage = styled.div `
  padding: 1rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;
const MemoDashboard = () => {
    const authStore = useAuthStore();
    const { user } = authStore;
    const [memos, setMemos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    // Sorting
    const [sortKey, setSortKey] = useState('requestedAt');
    const [sortDirection, setSortDirection] = useState('desc');
    // Modal
    const [selectedMemo, setSelectedMemo] = useState(null);
    useEffect(() => {
        if (authStore.isAuthenticated && !authStore.isLoading) {
            loadMemos();
        }
    }, [authStore.isAuthenticated, authStore.isLoading]);
    const loadMemos = async () => {
        try {
            setIsLoading(true);
            setError('');
            const response = await apiClient.get('memos/my-requests');
            if (response.ok && response.data?.success) {
                setMemos(response.data.memos || []);
            }
            else {
                setError(response.data?.error || response.error || 'Failed to load memos');
            }
        }
        catch (err) {
            console.error('Failed to load memos:', err);
            setError('Failed to load memo requests');
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleSort = (key) => {
        if (sortKey === key) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            }
            else if (sortDirection === 'desc') {
                setSortKey(null);
                setSortDirection(null);
            }
        }
        else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };
    const handleCancelMemo = async (memoId) => {
        if (!window.confirm('Are you sure you want to cancel this memo request?'))
            return;
        try {
            const response = await apiClient.put(`memos/${memoId}/status`, { status: MemoStatus.CANCELLED });
            if (response.ok) {
                await loadMemos();
                notify.success('Memo Cancelled', 'Your memo request has been cancelled');
            }
            else {
                notify.error('Cancel Failed', response.error || 'Failed to cancel memo');
            }
        }
        catch (err) {
            console.error('Cancel failed:', err);
            notify.error('Cancel Failed', 'Failed to cancel memo request');
        }
    };
    // Filter and sort memos
    const filteredMemos = React.useMemo(() => {
        let result = [...memos];
        // Apply status filter
        if (statusFilter !== 'all') {
            result = result.filter(m => m.status === statusFilter);
        }
        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter(m => m.diamondId.toLowerCase().includes(query) ||
                m.diamondDetails.stockNumber?.toLowerCase().includes(query) ||
                m.diamondDetails.certificateNumber?.toLowerCase().includes(query) ||
                m.diamondDetails.shape?.toLowerCase().includes(query));
        }
        // Apply sorting
        if (sortKey && sortDirection) {
            result.sort((a, b) => {
                let aVal, bVal;
                switch (sortKey) {
                    case 'requestedAt':
                        aVal = new Date(a.requestedAt).getTime();
                        bVal = new Date(b.requestedAt).getTime();
                        break;
                    case 'dueDate':
                        aVal = a.dueDate ? new Date(a.dueDate).getTime() : 0;
                        bVal = b.dueDate ? new Date(b.dueDate).getTime() : 0;
                        break;
                    case 'price':
                        aVal = a.diamondDetails.priceUSD || 0;
                        bVal = b.diamondDetails.priceUSD || 0;
                        break;
                    case 'status':
                        aVal = a.status;
                        bVal = b.status;
                        break;
                    case 'carat':
                        aVal = a.diamondDetails.caratWeight || 0;
                        bVal = b.diamondDetails.caratWeight || 0;
                        break;
                    default:
                        return 0;
                }
                if (aVal < bVal)
                    return sortDirection === 'asc' ? -1 : 1;
                if (aVal > bVal)
                    return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return result;
    }, [memos, statusFilter, searchQuery, sortKey, sortDirection]);
    // Calculate stats
    const stats = MemoHelpers.calculateMemoStatistics(memos);
    if (isLoading) {
        return (_jsxs(_Fragment, { children: [_jsxs(HeroSection, { children: [_jsxs(HeroTitle, { children: ["My ", _jsx("span", { children: "Memos" })] }), _jsx(HeroSubtitle, { children: "Loading your memo requests..." })] }), _jsx(MainContent, { children: _jsxs(LoadingContainer, { children: [_jsx(FaSync, { className: "spin", style: { fontSize: '2rem', marginBottom: '1rem' } }), _jsx("span", { children: "Loading memos..." })] }) })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs(HeroSection, { children: [_jsxs(HeroTitle, { children: ["My ", _jsx("span", { children: "Memos" })] }), _jsx(HeroSubtitle, { children: "Track your diamond memo requests and viewing status" })] }), _jsxs(StatsGrid, { id: "tour-memo-stats", children: [_jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: stats.total }), _jsx("div", { className: "stat-label", children: "Total Requests" })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: stats.byStatus[MemoStatus.PENDING] || 0 }), _jsx("div", { className: "stat-label", children: "Pending" })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: (stats.byStatus[MemoStatus.APPROVED] || 0) + (stats.byStatus[MemoStatus.WITH_CLIENT] || 0) }), _jsx("div", { className: "stat-label", children: "Active" })] }), _jsxs(StatCard, { children: [_jsx("div", { className: "stat-value", children: stats.byStatus[MemoStatus.SOLD] || 0 }), _jsx("div", { className: "stat-label", children: "Completed" })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: ["$", (stats.totalValue / 1000).toFixed(0), "K"] }), _jsx("div", { className: "stat-label", children: "Total Value" })] })] }), _jsxs(MainContent, { children: [_jsx(Section, { children: _jsxs(FiltersContainer, { id: "tour-search-sort", children: [_jsxs(FilterGroup, { children: [_jsx(Label, { children: "Search" }), _jsx(Input, { type: "text", placeholder: "Search by diamond ID, stock #, certificate...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value) })] }), _jsxs(FilterGroup, { children: [_jsx(Label, { children: "Status" }), _jsxs(Select, { value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All Status" }), Object.values(MemoStatus).map(status => (_jsx("option", { value: status, children: MEMO_STATUS_LABELS[status] }, status)))] })] }), _jsxs(FilterGroup, { style: { justifyContent: 'flex-end' }, children: [_jsx(Label, { children: "\u00A0" }), _jsxs(Button, { onClick: loadMemos, children: [_jsx(FaSync, { style: { marginRight: '0.5rem' } }), " Refresh"] })] })] }) }), error && (_jsxs(ErrorMessage, { children: [_jsx(FaExclamationTriangle, {}), error] })), _jsxs(Section, { children: [_jsx(SectionHeader, { children: _jsxs(SectionTitle, { children: ["Memo Requests", _jsxs(ResultsCount, { children: ["(", filteredMemos.length, ")"] })] }) }), filteredMemos.length === 0 ? (_jsxs(EmptyState, { children: [_jsx(EmptyStateIcon, { children: _jsx(FaGem, {}) }), _jsx(EmptyStateText, { children: statusFilter === 'all'
                                            ? "You haven't made any memo requests yet. Browse our inventory to request diamonds."
                                            : `No memos with status: ${MEMO_STATUS_LABELS[statusFilter]}` })] })) : (_jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Memo ID" }), _jsx(SortableTh, { sortKey: "requestedAt", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Requested" }), _jsx(Th, { children: "Diamond" }), _jsx(SortableTh, { sortKey: "carat", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Carat" }), _jsx(SortableTh, { sortKey: "price", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Price" }), _jsx(SortableTh, { sortKey: "status", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Status" }), _jsx(SortableTh, { sortKey: "dueDate", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Due Date" }), _jsx(Th, { children: "Certificate" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: filteredMemos.map((memo, index) => {
                                                const isOverdue = MemoHelpers.isMemoOverdue(memo);
                                                const daysRemaining = MemoHelpers.getDaysRemaining(memo);
                                                const certUrl = memo.diamondDetails.certificateNumber && memo.diamondDetails.lab
                                                    ? getCertificateUrl(memo.diamondDetails.lab, memo.diamondDetails.certificateNumber)
                                                    : null;
                                                const canCancel = memo.status === MemoStatus.PENDING;
                                                const isFirstRow = index === 0;
                                                return (_jsxs(Tr, { id: isFirstRow ? 'tour-memo-card' : undefined, children: [_jsx(Td, { children: _jsxs("strong", { children: ["#", memo.id.slice(0, 8).toUpperCase()] }) }), _jsx(Td, { children: new Date(memo.requestedAt).toLocaleDateString() }), _jsx(Td, { children: _jsxs(DiamondCell, { children: [_jsx(DiamondTitle, { children: memo.diamondDetails.shape }), _jsxs(DiamondSpecs, { children: [memo.diamondDetails.color, " / ", memo.diamondDetails.clarity, memo.diamondDetails.cut && ` / ${memo.diamondDetails.cut}`] })] }) }), _jsxs(Td, { children: [memo.diamondDetails.caratWeight, "ct"] }), _jsx(Td, { children: _jsxs(PriceCell, { children: ["$", (memo.diamondDetails.priceUSD || 0).toLocaleString()] }) }), _jsxs(Td, { children: [_jsx(MemoStatusBadge, { "$status": memo.status, children: MemoHelpers.getMemoStatusLabel(memo.status) }), isOverdue && _jsx(OverdueBadge, { children: "Overdue" })] }), _jsx(Td, { children: memo.dueDate ? (_jsxs(DateCell, { children: [_jsx("span", { children: new Date(memo.dueDate).toLocaleDateString() }), daysRemaining !== null && (_jsx(DaysIndicator, { "$overdue": daysRemaining < 0, "$urgent": daysRemaining >= 0 && daysRemaining <= 3, children: daysRemaining < 0 ? `${Math.abs(daysRemaining)}d overdue` : `${daysRemaining}d left` }))] })) : (_jsx("span", { style: { color: 'var(--text-tertiary)' }, children: "\u2014" })) }), _jsx(Td, { children: certUrl ? (_jsxs(CertLink, { href: certUrl, target: "_blank", rel: "noopener noreferrer", children: [memo.diamondDetails.lab, " ", _jsx(FaExternalLinkAlt, {})] })) : (_jsx("span", { style: { color: 'var(--text-tertiary)' }, children: "\u2014" })) }), _jsx(Td, { children: _jsxs(ActionButtonGroup, { id: isFirstRow ? 'tour-memo-actions' : undefined, children: [_jsxs(ActionButton, { onClick: () => setSelectedMemo(memo), children: [_jsx(FaEye, {}), " Details"] }), canCancel && (_jsxs(ActionButton, { "$danger": true, onClick: () => handleCancelMemo(memo.id), children: [_jsx(FaTimes, {}), " Cancel"] }))] }) })] }, memo.id));
                                            }) })] }) }))] })] }), selectedMemo && (_jsx(BaseModal, { isOpen: true, onClose: () => setSelectedMemo(null), title: "Memo Request Details", maxWidth: "600px", children: _jsxs("div", { style: { padding: '8px 0' }, children: [_jsxs("div", { style: { marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid var(--border-color)' }, children: [_jsxs("h3", { style: { fontFamily: "'Playfair Display', serif", color: 'var(--text-primary)', fontSize: '20px', margin: '0 0 16px 0' }, children: [selectedMemo.diamondDetails.caratWeight, "ct ", selectedMemo.diamondDetails.shape, " Diamond"] }), _jsxs("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }, children: [_jsxs("div", { children: [_jsx("div", { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }, children: "Color / Clarity / Cut" }), _jsxs("div", { style: { color: 'var(--text-primary)', fontWeight: '600' }, children: [selectedMemo.diamondDetails.color, " / ", selectedMemo.diamondDetails.clarity, " / ", selectedMemo.diamondDetails.cut || 'N/A'] })] }), _jsxs("div", { children: [_jsx("div", { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }, children: "Certification" }), _jsxs("div", { style: { color: 'var(--text-primary)', fontWeight: '600' }, children: [selectedMemo.diamondDetails.lab, " ", selectedMemo.diamondDetails.certificateNumber] })] }), _jsxs("div", { children: [_jsx("div", { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }, children: "Stock Number" }), _jsx("div", { style: { color: 'var(--text-primary)', fontWeight: '600' }, children: selectedMemo.diamondDetails.stockNumber || 'N/A' })] }), _jsxs("div", { children: [_jsx("div", { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }, children: "Value" }), _jsxs("div", { style: { fontFamily: "'Playfair Display', serif", color: 'var(--accent-gold)', fontWeight: '600', fontSize: '18px' }, children: ["$", (selectedMemo.diamondDetails.priceUSD || 0).toLocaleString()] })] })] })] }), _jsxs("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }, children: [_jsxs("div", { children: [_jsx("div", { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }, children: "Status" }), _jsx(MemoStatusBadge, { "$status": selectedMemo.status, children: MemoHelpers.getMemoStatusLabel(selectedMemo.status) })] }), _jsxs("div", { children: [_jsx("div", { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }, children: "Requested" }), _jsx("div", { style: { color: 'var(--text-primary)' }, children: new Date(selectedMemo.requestedAt).toLocaleDateString() })] }), selectedMemo.dueDate && (_jsxs("div", { children: [_jsx("div", { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }, children: "Due Date" }), _jsxs("div", { style: { color: 'var(--text-primary)' }, children: [new Date(selectedMemo.dueDate).toLocaleDateString(), MemoHelpers.isMemoOverdue(selectedMemo) && (_jsx("span", { style: { color: '#ef4444', marginLeft: '8px', fontWeight: '700' }, children: "OVERDUE" }))] })] })), selectedMemo.purpose && (_jsxs("div", { children: [_jsx("div", { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }, children: "Purpose" }), _jsx("div", { style: { color: 'var(--text-primary)', textTransform: 'capitalize' }, children: selectedMemo.purpose })] }))] }), selectedMemo.dealerNotes && (_jsxs("div", { style: { marginTop: '24px', padding: '12px', background: 'var(--bg-tertiary)', borderLeft: '3px solid var(--accent-gold)', borderRadius: '4px' }, children: [_jsx("div", { style: { fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase' }, children: "Your Notes" }), _jsx("div", { style: { color: 'var(--text-primary)', fontSize: '14px' }, children: selectedMemo.dealerNotes })] }))] }) }))] }));
};
export default MemoDashboard;
